<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <v-app>
    <!-- Top Navigation Bar -->
    <v-app-bar :color="colors.primaryDark" dark elevation="4" height="60">
      <div class="d-flex align-center">
        <v-icon size="40" class="ml-7 mr-2 pulse">fas fa-building</v-icon>
        <span class="text-h6 font-weight-bold gradient-text">
          SICON PAINEL DE CONTROLE I.A
        </span>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center" style="gap: 24px">
        <!-- User Profile -->
        <div class="d-flex align-center mr-12 user-profile" style="gap: 7px">
          <v-avatar size="40" :color="colors.primaryLight" class="profile-avatar">
            <v-icon color="white" size="20">fas fa-user</v-icon>
          </v-avatar>
          <div class="d-flex flex-column">
            <span class="font-weight-bold text-body-2">
              {{ userDetails?.name || 'Carregando...' }}
            </span>
            <span class="text-caption opacity-75">
              {{ userDetails?.department?.name || 'Online' }}
            </span>
          </div>
        </div>
      </div>
    </v-app-bar>

    <!-- Sidebar Navigation -->
    <v-navigation-drawer
      v-model="drawer"
      :color="colors.primaryDark"
      dark
      permanent
      width="250"
      class="custom-drawer"
    >
      <!-- User Profile in Sidebar -->
      <div class="pa-4 sidebar-profile" style="border-bottom: 1px solid #1a4b7a">
        <div class="d-flex align-center" style="gap: 12px">
          <v-avatar size="45" :color="colors.primaryLight" class="profile-avatar">
            <v-icon color="white" size="22">fas fa-user</v-icon>
          </v-avatar>
          <div>
            <div class="font-weight-bold text-body-2 lowercase">
              {{ userDetails?.name || 'Carregando...' }}
            </div>
            <div class="text-caption opacity-75">
              {{ userDetails?.department?.name || 'Sistema Online' }}
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation Menu -->
      <v-list nav class="pa-0 text-start">
        <v-list-item
          v-for="item in menuItems"
          :key="item.title"
          :value="item.value"
          :active="item.active"
          rounded="xl"
          class="mb-2 menu-item"
          :class="item.active ? 'active-item' : ''"
          lines="one"
          density="comfortable"
          @click="router.push(item.route)"
        >
          <template #prepend>
            <v-icon size="22" class="mr-2">{{ item.icon }}</v-icon>
          </template>

          <template #title>
            <span class="item-title">{{ item.title }}</span>
          </template>

          <template #append v-if="item.badge">
            <v-chip size="small" :color="item.badgeColor" class="pulse-badge">
              {{ item.badge }}
            </v-chip>
          </template>
        </v-list-item>
      </v-list>

      <template v-slot:append>
        <div class="pa-3" style="border-top: 1px solid #1a4b7a">
          <v-btn
            block
            variant="outlined"
            prepend-icon="fas fa-sign-out-alt"
            class="logout-btn"
            @click="logout"
          >
            Sair
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- Main Content - File Navigation System -->
    <v-main class="bg-grey-lighten-5 main-content" style="margin-top: 20px">
      <v-container fluid class="pa-6">
        <!-- Header with Breadcrumb and Actions -->
        <v-card flat class="mb-4 file-header" :color="colors.lightBg">
          <v-card-text>
            <div class="d-flex justify-space-between align-center flex-wrap" style="gap: 12px">
              <div>
                <v-breadcrumbs :items="breadcrumbs" class="pa-0">
                  <template v-slot:item="{ item }">
                    <v-btn
                      variant="text"
                      size="small"
                      @click="navigateToPath(item.path)"
                      :color="colors.primaryDark"
                    >
                      <v-icon v-if="item.icon" size="18" class="mr-1">{{ item.icon }}</v-icon>
                      {{ item.title }}
                    </v-btn>
                  </template>
                  <template v-slot:divider>
                    <v-icon size="16">fas fa-chevron-right</v-icon>
                  </template>
                </v-breadcrumbs>
                <div class="text-caption text-grey-darken-1 current-path">
                  Caminho atual: {{ currentPath }}
                </div>
              </div>

              <div class="d-flex" style="gap: 12px">
                <v-btn
                  :color="colors.primaryGreen"
                  prepend-icon="fas fa-folder-plus"
                  variant="elevated"
                  size="small"
                  @click="openCreateFolderDialog"
                >
                  Nova pasta
                </v-btn>
                <v-btn
                  :color="colors.primaryDark"
                  prepend-icon="fas fa-upload"
                  variant="elevated"
                  size="small"
                  @click="openUploadPicker"
                >
                  Upload
                </v-btn>
                <v-btn
                  :color="colors.primaryLight"
                  prepend-icon="fas fa-sync-alt"
                  @click="refreshCurrentPath"
                  variant="elevated"
                  size="small"
                >
                  Atualizar
                </v-btn>
              </div>
            </div>
          </v-card-text>
        </v-card>
        <input
          ref="fileInputRef"
          type="file"
          class="d-none"
          multiple
          @change="handleFileSelection"
        >

        <!-- Loading State -->
        <v-card v-if="loading" flat class="pa-8 text-center">
          <v-progress-circular
            :color="colors.primaryLight"
            indeterminate
            size="64"
          ></v-progress-circular>
          <div class="mt-4 text-h6">Carregando arquivos...</div>
        </v-card>

        <!-- File Explorer Grid -->
        <v-row v-else-if="hasItems">
          <!-- Folders -->
          <v-col
            v-for="folder in currentContent.folders"
            :key="`folder-${folder.path}`"
            cols="6"
            sm="4"
            md="3"
            lg="2"
          >
            <v-card
              class="file-item folder-card"
              @click="openFolder(folder)"
              hover
              :elevation="hoveredItem === `folder-${folder.path}` ? 8 : 2"
              @mouseenter="hoveredItem = `folder-${folder.path}`"
              @mouseleave="hoveredItem = null"
            >
              <v-card-text class="text-center pa-4">
                <v-icon
                  size="48"
                  :color="colors.primaryGreen"
                  class="mb-2 folder-icon"
                >
                  fas fa-folder
                </v-icon>
                <div class="text-body-2 font-weight-medium item-name">
                  {{ folder.name }}
                </div>
                <div class="text-caption text-grey-darken-1 item-meta">
                  Atualizado em {{ formatDate(folder.mtimeMs) }}
                </div>
                <div class="d-flex justify-center mt-2 file-actions" style="gap: 8px">
                  <v-btn
                    size="x-small"
                    color="red-darken-1"
                    variant="tonal"
                    @click.stop="removeItem(folder)"
                  >
                    <v-icon size="12" class="mr-1">fas fa-trash</v-icon>
                    Excluir
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Files -->
          <v-col
            v-for="file in currentContent.files"
            :key="`file-${file.path}`"
            cols="6"
            sm="4"
            md="3"
            lg="2"
          >
            <v-card
              class="file-item file-card"
              hover
              :elevation="hoveredItem === `file-${file.path}` ? 8 : 2"
              @mouseenter="hoveredItem = `file-${file.path}`"
              @mouseleave="hoveredItem = null"
            >
              <v-card-text class="text-center pa-4">
                <v-icon
                  size="48"
                  :color="getFileIconColor(file)"
                  class="mb-2 file-icon"
                >
                  {{ getFileIcon(file) }}
                </v-icon>
                <div class="text-body-2 font-weight-medium item-name">
                  {{ file.name }}
                </div>
                <div class="text-caption text-grey-darken-1 item-meta">
                  {{ getFileTypeLabel(file) }}
                </div>
                <div class="text-caption text-grey-darken-1 item-meta">
                  {{ formatFileSize(file.size) }}
                </div>
                <div class="text-caption text-grey-darken-1 item-meta">
                  {{ formatDate(file.mtimeMs) }}
                </div>
                <div class="d-flex justify-center mt-2 file-actions" style="gap: 8px">
                  <v-btn
                    size="x-small"
                    :color="colors.primaryLight"
                    variant="tonal"
                    @click.stop="previewFile(file)"
                  >
                    <v-icon size="12" class="mr-1">fas fa-eye</v-icon>
                    Visualizar
                  </v-btn>
                  <v-btn
                    size="x-small"
                    :color="colors.primaryDark"
                    variant="tonal"
                    @click.stop="downloadFile(file)"
                  >
                    <v-icon size="12" class="mr-1">fas fa-download</v-icon>
                    Baixar
                  </v-btn>
                  <v-btn
                    size="x-small"
                    color="red-darken-1"
                    variant="tonal"
                    @click.stop="removeItem(file)"
                  >
                    <v-icon size="12" class="mr-1">fas fa-trash</v-icon>
                    Excluir
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Empty State -->
        <v-card
          v-else
          flat
          class="pa-8 text-center empty-state"
        >
          <v-icon size="64" color="grey-lighten-1" class="mb-4">
            fas fa-folder-open
          </v-icon>
          <div class="text-h6 text-grey">Pasta vazia</div>
          <div class="text-body-2 text-grey mt-2">
            Nenhum arquivo ou pasta foi encontrado em {{ currentPath }}
          </div>
        </v-card>

        <v-dialog v-model="createFolderDialog" max-width="460">
          <v-card>
            <v-card-title class="text-h6">Criar pasta</v-card-title>
            <v-card-text>
              <div class="text-caption text-grey-darken-1 mb-3">
                Pasta atual: {{ currentPath }}
              </div>
              <v-text-field
                v-model="newFolderName"
                label="Nome da pasta"
                density="comfortable"
                variant="outlined"
                :disabled="creatingFolder"
                @keyup.enter="handleCreateFolder"
              ></v-text-field>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn variant="text" :disabled="creatingFolder" @click="closeCreateFolderDialog">
                Cancelar
              </v-btn>
              <v-btn
                :color="colors.primaryDark"
                :loading="creatingFolder"
                :disabled="!newFolderName.trim()"
                @click="handleCreateFolder"
              >
                Criar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="uploadDialog" max-width="560">
          <v-card>
            <v-card-title class="text-h6">Upload de documentos</v-card-title>
            <v-card-text>
              <div class="text-caption text-grey-darken-1 mb-3">
                Destino: {{ currentPath }}
              </div>
              <div class="text-caption text-grey-darken-1 mb-2">
                {{ selectedUploadFiles.length }} arquivo(s) selecionado(s)
              </div>
              <div class="selected-files mb-3" v-if="selectedUploadFiles.length > 0">
                <v-chip
                  v-for="file in selectedUploadFiles"
                  :key="`${file.name}-${file.size}`"
                  size="small"
                  variant="outlined"
                  class="mr-1 mb-1"
                >
                  {{ file.name }}
                </v-chip>
              </div>
              <v-switch
                v-model="uploadOverwrite"
                color="primary"
                density="compact"
                hide-details
                label="Sobrescrever arquivos existentes"
              ></v-switch>
            </v-card-text>
            <v-card-actions>
              <v-btn
                variant="text"
                :disabled="uploadingFiles"
                @click="openUploadPicker"
              >
                Selecionar novamente
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn variant="text" :disabled="uploadingFiles" @click="closeUploadDialog">
                Cancelar
              </v-btn>
              <v-btn
                :color="colors.primaryDark"
                :loading="uploadingFiles"
                :disabled="selectedUploadFiles.length === 0"
                @click="handleUploadDocs"
              >
                Enviar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Snackbar for notifications -->
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
          {{ snackbar.message }}
          <template v-slot:actions>
            <v-btn variant="text" @click="snackbar.show = false">Fechar</v-btn>
          </template>
        </v-snackbar>
      </v-container>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { menuItems } from '@/constants/MenuItems';
import { usePermissions } from '@/composables/usePermissions';

const router = useRouter();
const authStore = useAuthStore();
const {
  userDetails,
  checkPermissionAndRedirect
} = usePermissions();

const API_BASE = (
  process.env.VUE_APP_EXPLORER_API_URL
  || process.env.VUE_APP_API_URL
  || ''
)
  .replace(/\/private-files\/?$/, '')
  .replace(/\/$/, '');
const LIST_ENDPOINT = '/explorer-files';
const CREATE_FOLDER_ENDPOINT = '/explorer-folders';
const UPLOAD_ENDPOINT = '/upload-docs';
const DOWNLOAD_ENDPOINT = '/download-files';
const STREAM_ENDPOINT = '/stream-files';
const NAVIGATION_DEBOUNCE_MS = 200;

const ERROR_MESSAGES = {
  INVALID_PATH: 'Caminho inválido ou não permitido.',
  PATH_REQUIRED: 'Informe o caminho para continuar.',
  ROOT_PROTECTED: 'A raiz não pode ser excluída.',
  DIR_NOT_EMPTY: 'A pasta não está vazia. Use exclusão recursiva.',
  NOT_FOUND: 'Item não encontrado.',
  NO_FILES: 'Nenhum arquivo foi enviado.',
  INVALID_FILENAME: 'Um ou mais arquivos possuem nome inválido.',
  FILES_ALREADY_EXIST: 'Todos os arquivos selecionados já existem nesta pasta.',
  LIST_FAILED: 'Não foi possível listar esta pasta.',
  CREATE_FOLDER_FAILED: 'Não foi possível criar a pasta.',
  UPLOAD_FAILED: 'Falha ao enviar arquivos.',
  REMOVE_FAILED: 'Falha ao excluir item.',
  STREAM_FAILED: 'Falha ao abrir visualização do arquivo.'
};

// Data refs
const drawer = ref(true);
const loading = ref(false);
const hoveredItem = ref(null);
const currentPath = ref('/');
const currentContent = ref({ folders: [], files: [] });
const createFolderDialog = ref(false);
const newFolderName = ref('');
const creatingFolder = ref(false);
const fileInputRef = ref(null);
const uploadDialog = ref(false);
const selectedUploadFiles = ref([]);
const uploadOverwrite = ref(false);
const uploadingFiles = ref(false);

const snackbar = ref({
  show: false,
  message: '',
  color: 'success'
});

// Colors
const colors = {
  primaryDark: '#123A5D',
  primaryLight: '#57C2E3',
  primaryGreen: '#A3C940',
  lightBg: '#E7F2F9'
};

let navigationTimer = null;
let activeRequestId = 0;

const hasItems = computed(() => {
  return currentContent.value.folders.length > 0 || currentContent.value.files.length > 0;
});

const breadcrumbs = computed(() => {
  const normalized = normalizePath(currentPath.value);
  const crumbs = [
    {
      title: '/',
      icon: 'fas fa-home',
      path: '/'
    }
  ];

  if (normalized === '/') {
    return crumbs;
  }

  let builtPath = '';
  normalized
    .split('/')
    .filter(Boolean)
    .forEach(segment => {
      builtPath += `/${segment}`;
      crumbs.push({
        title: segment,
        path: builtPath
      });
    });

  return crumbs;
});

const normalizePath = (path) => {
  if (!path || path === '') {
    return '/';
  }

  let normalized = path;
  if (!normalized.startsWith('/')) {
    normalized = `/${normalized}`;
  }
  normalized = normalized.replace(/\/{2,}/g, '/');

  if (normalized.length > 1 && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1);
  }

  return normalized;
};

const buildChildPath = (basePath, childName) => {
  const safeChildName = String(childName || '').trim().replace(/^\/+|\/+$/g, '');
  return normalizePath(`${normalizePath(basePath)}/${safeChildName}`);
};

const buildUrl = (endpoint, query = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    searchParams.set(key, String(value));
  });

  const queryString = searchParams.toString();
  return `${API_BASE}${endpoint}${queryString ? `?${queryString}` : ''}`;
};

const getErrorMessage = (errorCode) => {
  return ERROR_MESSAGES[errorCode] || 'Ocorreu um erro ao acessar os arquivos. Tente novamente.';
};

const getErrorCode = (error) => {
  return error?.code || error?.message || 'UNKNOWN';
};

const buildApiError = (code, payload = {}) => {
  const error = new Error(code);
  error.code = code;
  error.payload = payload;
  return error;
};

const parseResponseBody = async (response) => {
  return response.json().catch(() => ({}));
};

const splitItems = (items = []) => {
  return items.reduce((accumulator, item) => {
    if (item.type === 'dir') {
      accumulator.folders.push(item);
    } else if (item.type === 'file') {
      accumulator.files.push(item);
    }

    return accumulator;
  }, { folders: [], files: [] });
};

const listFiles = async (path = '/') => {
  const response = await fetch(buildUrl(LIST_ENDPOINT, {
    path: normalizePath(path)
  }));
  const body = await parseResponseBody(response);

  if (!response.ok) {
    throw buildApiError(body.error || 'LIST_FAILED', body);
  }

  return body;
};

const createFolder = async (path) => {
  const response = await fetch(`${API_BASE}${CREATE_FOLDER_ENDPOINT}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path: normalizePath(path) })
  });
  const body = await parseResponseBody(response);

  if (!response.ok) {
    throw buildApiError(body.error || 'CREATE_FOLDER_FAILED', body);
  }

  return body;
};

const uploadDocs = async (path, files, overwrite = false) => {
  const formData = new FormData();
  files.forEach(file => formData.append('docs', file));
  formData.append('path', normalizePath(path));
  formData.append('overwrite', String(overwrite));

  const response = await fetch(`${API_BASE}${UPLOAD_ENDPOINT}`, {
    method: 'POST',
    body: formData
  });
  const body = await parseResponseBody(response);

  if (!response.ok) {
    throw buildApiError(body.error || 'UPLOAD_FAILED', body);
  }

  return body;
};

const removePath = async (path, recursive = false) => {
  const response = await fetch(buildUrl(LIST_ENDPOINT, {
    path: normalizePath(path),
    recursive: String(Boolean(recursive))
  }), {
    method: 'DELETE'
  });
  const body = await parseResponseBody(response);

  if (!response.ok) {
    throw buildApiError(body.error || 'REMOVE_FAILED', body);
  }

  return body;
};

const loadFolders = async (path = '/') => {
  const requestId = ++activeRequestId;
  loading.value = true;

  try {
    const normalizedPath = normalizePath(path);
    const data = await listFiles(normalizedPath);

    if (requestId !== activeRequestId) {
      return;
    }

    const content = splitItems(data.items || []);
    currentPath.value = normalizePath(data.path || normalizedPath);
    currentContent.value = content;
  } catch (error) {
    if (requestId !== activeRequestId) {
      return;
    }

    showSnackbar(getErrorMessage(getErrorCode(error)), 'error');
    currentContent.value = { folders: [], files: [] };
  } finally {
    if (requestId === activeRequestId) {
      loading.value = false;
    }
  }
};

const navigateToPath = (path) => {
  if (navigationTimer) {
    clearTimeout(navigationTimer);
  }

  const normalizedPath = normalizePath(path);
  navigationTimer = setTimeout(() => {
    loadFolders(normalizedPath);
    navigationTimer = null;
  }, NAVIGATION_DEBOUNCE_MS);
};

const refreshCurrentPath = async () => {
  if (navigationTimer) {
    clearTimeout(navigationTimer);
    navigationTimer = null;
  }

  await loadFolders(currentPath.value);
};

const openFolder = (folder) => {
  navigateToPath(folder.path);
};

const getDownloadUrl = (path) => {
  return buildUrl(DOWNLOAD_ENDPOINT, { path: normalizePath(path) });
};

const getStreamUrl = (path) => {
  return buildUrl(STREAM_ENDPOINT, { path: normalizePath(path) });
};

const downloadFile = (file) => {
  const anchor = document.createElement('a');
  anchor.href = getDownloadUrl(file.path);
  anchor.rel = 'noopener';
  anchor.download = file.name;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

const previewFile = (file) => {
  const previewWindow = window.open(
    getStreamUrl(file.path),
    '_blank',
    'noopener,noreferrer'
  );

  if (!previewWindow) {
    showSnackbar('Falha ao abrir visualização do arquivo.', 'error');
  }
};

const openCreateFolderDialog = () => {
  newFolderName.value = '';
  createFolderDialog.value = true;
};

const closeCreateFolderDialog = () => {
  if (creatingFolder.value) {
    return;
  }

  newFolderName.value = '';
  createFolderDialog.value = false;
};

const handleCreateFolder = async () => {
  const folderName = newFolderName.value.trim().replace(/^\/+|\/+$/g, '');
  if (!folderName) {
    showSnackbar('Informe um nome para a pasta.', 'warning');
    return;
  }

  const targetPath = buildChildPath(currentPath.value, folderName);
  creatingFolder.value = true;
  try {
    await createFolder(targetPath);
    createFolderDialog.value = false;
    newFolderName.value = '';
    showSnackbar('Pasta criada com sucesso.');
    await refreshCurrentPath();
  } catch (error) {
    showSnackbar(getErrorMessage(getErrorCode(error)), 'error');
  } finally {
    creatingFolder.value = false;
  }
};

const openUploadPicker = () => {
  if (!fileInputRef.value) {
    return;
  }

  fileInputRef.value.value = '';
  fileInputRef.value.click();
};

const handleFileSelection = (event) => {
  const files = Array.from(event?.target?.files || []);
  if (files.length === 0) {
    return;
  }

  if (files.length > 20) {
    showSnackbar('A seleção foi limitada aos primeiros 20 arquivos.', 'warning');
  }

  selectedUploadFiles.value = files.slice(0, 20);
  uploadDialog.value = true;
};

const resetUploadData = () => {
  selectedUploadFiles.value = [];
  uploadOverwrite.value = false;
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
};

const closeUploadDialog = () => {
  if (uploadingFiles.value) {
    return;
  }

  uploadDialog.value = false;
  resetUploadData();
};

const handleUploadDocs = async () => {
  if (selectedUploadFiles.value.length === 0) {
    showSnackbar('Selecione ao menos um arquivo para enviar.', 'warning');
    return;
  }

  uploadingFiles.value = true;
  try {
    const result = await uploadDocs(currentPath.value, selectedUploadFiles.value, uploadOverwrite.value);
    const uploadedCount = Array.isArray(result.uploaded) ? result.uploaded.length : 0;
    const conflictsCount = Array.isArray(result.conflicts) ? result.conflicts.length : 0;

    if (uploadedCount > 0 && conflictsCount > 0) {
      showSnackbar(`Upload parcial: ${uploadedCount} enviado(s), ${conflictsCount} conflito(s).`, 'warning');
    } else if (uploadedCount > 0) {
      showSnackbar(`Upload concluído: ${uploadedCount} arquivo(s) enviado(s).`);
    } else if (conflictsCount > 0) {
      showSnackbar(`Nenhum arquivo enviado. ${conflictsCount} conflito(s) detectado(s).`, 'warning');
    } else {
      showSnackbar('Upload concluído.');
    }

    uploadDialog.value = false;
    resetUploadData();
    await refreshCurrentPath();
  } catch (error) {
    const errorCode = getErrorCode(error);
    showSnackbar(getErrorMessage(errorCode), errorCode === 'FILES_ALREADY_EXIST' ? 'warning' : 'error');
  } finally {
    uploadingFiles.value = false;
  }
};

const removeItem = async (item) => {
  const isDirectory = item.type === 'dir';
  const itemLabel = isDirectory ? 'pasta' : 'arquivo';

  const shouldRemove = window.confirm(`Deseja excluir ${itemLabel} "${item.name}"?`);
  if (!shouldRemove) {
    return;
  }

  try {
    await removePath(item.path, false);
    showSnackbar(`${itemLabel} removido com sucesso.`);
    await refreshCurrentPath();
  } catch (error) {
    const errorCode = getErrorCode(error);
    if (errorCode === 'DIR_NOT_EMPTY' && isDirectory) {
      const shouldRemoveRecursive = window.confirm(
        `A pasta "${item.name}" não está vazia. Deseja excluir também o conteúdo interno?`
      );
      if (!shouldRemoveRecursive) {
        return;
      }

      try {
        await removePath(item.path, true);
        showSnackbar('Pasta removida com todo o conteúdo.');
        await refreshCurrentPath();
      } catch (recursiveError) {
        showSnackbar(getErrorMessage(getErrorCode(recursiveError)), 'error');
      }
      return;
    }

    showSnackbar(getErrorMessage(errorCode), 'error');
  }
};

const getFileExtension = (fileName = '') => {
  const parts = fileName.toLowerCase().split('.');
  if (parts.length <= 1) {
    return '';
  }

  return parts.pop();
};

const getFileIcon = (file) => {
  const mime = (file?.mime || '').toLowerCase();
  const extension = getFileExtension(file?.name);

  if (mime.includes('pdf') || extension === 'pdf') {
    return 'fas fa-file-pdf';
  }
  if (
    mime.startsWith('image/') ||
    ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(extension)
  ) {
    return 'fas fa-file-image';
  }
  if (
    mime.includes('spreadsheet') ||
    ['xls', 'xlsx', 'csv'].includes(extension)
  ) {
    return 'fas fa-file-excel';
  }
  if (
    mime.includes('word') ||
    ['doc', 'docx', 'odt'].includes(extension)
  ) {
    return 'fas fa-file-word';
  }
  if (
    mime.includes('zip') ||
    ['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)
  ) {
    return 'fas fa-file-archive';
  }
  if (
    mime.startsWith('video/') ||
    ['mp4', 'avi', 'mov', 'mkv'].includes(extension)
  ) {
    return 'fas fa-file-video';
  }

  return 'fas fa-file';
};

const getFileIconColor = (file) => {
  const icon = getFileIcon(file);
  const iconColors = {
    'fas fa-file-pdf': '#d32f2f',
    'fas fa-file-image': '#f57c00',
    'fas fa-file-excel': '#2e7d32',
    'fas fa-file-word': '#1565c0',
    'fas fa-file-archive': '#546e7a',
    'fas fa-file-video': '#7b1fa2',
    'fas fa-file': '#757575'
  };

  return iconColors[icon] || '#757575';
};

const getFileTypeLabel = (file) => {
  if (file.mime) {
    return file.mime;
  }

  const extension = getFileExtension(file.name);
  return extension ? extension.toUpperCase() : 'arquivo';
};

const formatFileSize = (size) => {
  if (typeof size !== 'number' || Number.isNaN(size)) {
    return 'Tamanho indisponível';
  }

  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  if (size < 1024 * 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const formatDate = (mtimeMs) => {
  if (typeof mtimeMs !== 'number' || Number.isNaN(mtimeMs)) {
    return 'data indisponível';
  }

  return new Date(mtimeMs).toLocaleString('pt-BR');
};

const showSnackbar = (message, color = 'success') => {
  snackbar.value = {
    show: true,
    message,
    color
  };
};

const logout = () => {
  if (authStore && typeof authStore.logout === 'function') {
    authStore.logout();
  }
  if (router && typeof router.push === 'function') {
    router.push('/');
  }
};

onMounted(async () => {
  const hasAccess = await checkPermissionAndRedirect('files');
  if (!hasAccess) {
    return;
  }

  await loadFolders('/');
});

onBeforeUnmount(() => {
  if (navigationTimer) {
    clearTimeout(navigationTimer);
  }
});
</script>

<style scoped>
/* Animations */
@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Component Styles */
.gradient-text {
  background: linear-gradient(45deg, #57C2E3, #A3C940);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.pulse {
  animation: pulse 2s infinite;
}

.user-profile {
  transition: all 0.3s ease;
  border-radius: 12px;
  padding: 8px 12px;
}

.user-profile:hover {
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
}

.profile-avatar {
  border: 2px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.profile-avatar:hover {
  border-color: rgba(255, 255, 255, 0.6);
  transform: scale(1.05);
}

.custom-drawer {
  background: linear-gradient(180deg, #123A5D 0%, #0f2a42 100%);
}

.menu-item {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin: 4px 0;
  position: relative;
  overflow: hidden;
}

.menu-item:hover {
  background: rgba(255, 255, 255, 0.12) !important;
  transform: translateX(4px);
}

.active-item {
  background: linear-gradient(135deg, #57C2E3, #4aa3c1) !important;
  box-shadow: 0 4px 12px rgba(87, 194, 227, 0.3);
}

.active-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 4px;
  background: #A3C940;
}

.pulse-badge {
  animation: pulse 2s infinite;
}

.logout-btn {
  border-color: rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
}

.logout-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.6);
}

.main-content {
  background: linear-gradient(135deg, #f5f5f5 0%, #e8f4f8 100%);
}

.lowercase {
  text-transform: lowercase;
}

/* File Explorer Styles */
.file-header {
  border-radius: 12px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fbfd 100%);
  border: 1px solid rgba(87, 194, 227, 0.2);
}

.current-path {
  margin-left: 12px;
}

.file-item {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 12px;
  animation: fadeIn 0.3s ease;
}

.folder-card:hover {
  transform: translateY(-4px);
  background: linear-gradient(135deg, #f0f9ff 0%, #e6f7ff 100%);
}

.folder-card:hover .folder-icon {
  transform: scale(1.1);
}

.file-card:hover {
  transform: translateY(-4px);
  background: linear-gradient(135deg, #fffbf0 0%, #fff7e6 100%);
}

.file-card:hover .file-icon {
  transform: scale(1.1);
}

.folder-icon, .file-icon {
  transition: transform 0.3s ease;
}

.item-name {
  word-break: break-word;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-meta {
  line-height: 1.3;
}

.file-actions {
  flex-wrap: wrap;
}

.selected-files {
  max-height: 150px;
  overflow-y: auto;
}

.empty-state {
  border-radius: 16px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 2px dashed #e0e0e0;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .user-profile span:last-child {
    display: none;
  }
}

@media (max-width: 600px) {
  .gradient-text {
    font-size: 1rem;
  }

  .file-header .d-flex {
    flex-direction: column;
    gap: 16px;
  }

  .current-path {
    margin-left: 0;
  }
}
</style>
